import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useRequiredCustomerId } from "hooks/use-customer-id";
import { $getRoot, $insertNodes } from "lexical";
import { useCallback, useEffect } from "react";
import { MessagesAPI } from "../../../api/messages";
import { useRootStore } from "../../../store";
import { $createImageNode, ImageNode } from "../nodes/ImageNode";

// Helper function to find an ImageNode by uploadId
function $findImageNodeByUploadId(uploadId: string): ImageNode | null {
  const root = $getRoot();

  // Recursively search all descendants
  function searchNode(node: any): ImageNode | null {
    if (node instanceof ImageNode && node.getUploadId() === uploadId) {
      return node;
    }

    // If node has children, search them
    if (typeof node.getChildren === "function") {
      const children = node.getChildren();
      for (const child of children) {
        const found = searchNode(child);
        if (found) {
          return found;
        }
      }
    }

    return null;
  }

  return searchNode(root);
}

const ACCEPTABLE_IMAGE_TYPES = [
  "image/gif",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp"
];

// Helper function to get image dimensions from a File object
function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight
        });
      };

      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };

      img.src = e.target?.result as string;
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsDataURL(file);
  });
}

// Return natural image dimensions without scaling
function getNaturalDimensions(
  naturalWidth: number,
  naturalHeight: number
): { width: number; height: number } {
  return { width: naturalWidth, height: naturalHeight };
}

export default function ImagePlugin(): null {
  const [editor] = useLexicalComposerContext();
  const customerId = useRequiredCustomerId();
  const { addNotification } = useRootStore((state) => state.toastNotifications);

  const uploadImageWithRetry = useCallback(
    async (file: File, uploadId: string): Promise<boolean> => {
      // Generate proper filename for pasted images (they often have generic names like "image.png")
      const fileName =
        file.name ||
        `pasted-image-${Date.now()}.${file.type.split("/")[1] || "png"}`;

      const MAX_RETRY_ATTEMPTS = 2;
      const RETRY_DELAY_MS = 1000;

      for (let attempt = 0; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
        try {
          // Step 1: Get presigned URL
          const response = await MessagesAPI.createUploadUrl(
            fileName,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            customerId!
          );
          const { key, uploadUrl, fetchUrl } = response.body;

          // Step 2: Upload to S3
          await MessagesAPI.uploadImageToS3(uploadUrl, file, file.type);

          // Step 3: Update node with S3 URL
          editor.update(() => {
            const node = $findImageNodeByUploadId(uploadId);
            if (node instanceof ImageNode) {
              // Get writable node and update properties directly
              const writable = node.getWritable();
              writable.__src = fetchUrl;
              writable.__s3Key = key;
              writable.__isUploading = false;
            } else {
              useRootStore
                .getState()
                .error.appendErrorNotification(
                  "Bildnoden kunde inte hittas efter uppladdning. Bilden kommer inte att visas korrekt.",
                  ""
                );
            }
          });

          return true;
        } catch (error) {
          if (attempt < MAX_RETRY_ATTEMPTS) {
            // Exponential backoff: 1s, 2s
            await new Promise((resolve) =>
              setTimeout(resolve, RETRY_DELAY_MS * Math.pow(2, attempt))
            );
          }
        }
      }

      // All retries failed - remove the node entirely
      editor.update(() => {
        const node = $findImageNodeByUploadId(uploadId);
        if (node) {
          node.remove(); // Delete the node completely (no base64 fallback)
        }
      });

      addNotification(
        "error",
        "Uppladdning av bild misslyckades. Bilden kunde inte laddas upp."
      );

      return false;
    },
    [editor, customerId, addNotification]
  );

  const processImageFile = useCallback(
    async (file: File) => {
      // Generate unique uploadId that survives node recreation
      const uploadId = `upload-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

      try {
        // Get actual image dimensions from file
        const { width: naturalWidth, height: naturalHeight } = await getImageDimensions(file);

        // Use natural dimensions without scaling
        const { width, height } = getNaturalDimensions(naturalWidth, naturalHeight);

        // Insert node with placeholder but correct aspect ratio
        editor.update(() => {
          const imageNode = $createImageNode({
            altText: file.name,
            // Empty 1x1 transparent SVG as placeholder - avoids multi-MB base64 strings
            src: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="1" height="1"%3E%3C/svg%3E',
            isUploading: !!customerId,
            uploadId: uploadId,
            width: width,  // Calculated width preserving aspect ratio
            height: height // Calculated height preserving aspect ratio
          });
          $insertNodes([imageNode]);
        });

        // Start upload immediately (not in callback)
        if (customerId) {
          uploadImageWithRetry(file, uploadId);
        } else {
          // No upload context - remove the node (don't show anything)
          editor.update(() => {
            const node = $findImageNodeByUploadId(uploadId);
            if (node) {
              node.remove();
            }
          });
        }
      } catch (error) {
        console.error("Failed to process image file:", error);
        addNotification(
          "error",
          "Kunde inte läsa bildfilen. Vänligen försök igen."
        );
      }
    },
    [editor, customerId, uploadImageWithRetry, addNotification]
  );

  useEffect(() => {
    const handlePaste = (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items) {
        return;
      }

      // Check if clipboard contains HTML content (e.g., from Excel, Word, or web pages)
      // If HTML is present, skip image insertion as the image is likely just a screenshot
      // of the content that will be handled by the rich text paste handler
      const hasHtmlContent = Array.from(items).some(
        (item) => item.type === "text/html"
      );
      if (hasHtmlContent) {
        return;
      }

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (ACCEPTABLE_IMAGE_TYPES.includes(item.type)) {
          event.preventDefault();

          const file = item.getAsFile();
          if (!file) {
            continue;
          }

          processImageFile(file);
          break;
        }
      }
    };

    const handleDrop = (event: DragEvent) => {
      const files = event.dataTransfer?.files;
      if (!files || files.length === 0) {
        return;
      }

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (ACCEPTABLE_IMAGE_TYPES.includes(file.type)) {
          event.preventDefault();
          event.stopPropagation();

          processImageFile(file);
          break;
        }
      }
    };

    const handleDragOver = (event: DragEvent) => {
      event.preventDefault();
    };

    const rootElement = editor.getRootElement();
    if (rootElement) {
      rootElement.addEventListener("paste", handlePaste as EventListener);
      rootElement.addEventListener("drop", handleDrop as EventListener);
      rootElement.addEventListener("dragover", handleDragOver as EventListener);

      return () => {
        rootElement.removeEventListener("paste", handlePaste as EventListener);
        rootElement.removeEventListener("drop", handleDrop as EventListener);
        rootElement.removeEventListener(
          "dragover",
          handleDragOver as EventListener
        );
      };
    }
  }, [editor, processImageFile]);

  return null;
}
